import { useMutation } from '@vue/apollo-composable';
import { computed, ref } from 'vue';

import { isNewContentImage } from '@/components/community/utils/type-guards';
import { ContentImage } from '@/components/community/utils/types';
import {
  createCommunityImageUploadOptionsMutation,
  createProfileImageUploadOptionsMutation
} from '@/shared/graphql/gateway/community/mutations';
import { useCommunityDispatchUtils } from '@/shared/graphql/gateway/utils';
import {
  imageMimeTypeToImageType,
  isValidImageSize
} from '@/shared/utils/files';

export const useContentImageUpload = () => {
  const { dispatchMutationWithTosRetry } = useCommunityDispatchUtils();

  const { mutate: dispatchCreatePostImageOptions } = useMutation(
    createCommunityImageUploadOptionsMutation,
    {
      clientId: 'gateway'
    }
  );

  const validateAndUploadImages = async (images: ContentImage[]) => {
    const updatedImageUrlPromises = images.map(async (image) => {
      if (isNewContentImage(image)) {
        if (!isValidImageSize(image.file)) {
          throw new Error(`File "${image.file.name}" is too big.`);
        }

        const imageType = imageMimeTypeToImageType(image.file.type);

        if (!imageType) {
          throw new Error(
            `File "${image.file.name}" is of unsupported type, got "${image.file.type}".`
          );
        }

        const res = await dispatchMutationWithTosRetry(() =>
          dispatchCreatePostImageOptions({
            input: {
              image_type: imageType
            }
          })
        );

        if (res?.data?.community?.createImageUploadOptions) {
          const { file_url, upload_endpoint } =
            res.data.community.createImageUploadOptions;

          await uploadImageToS3(image.file, upload_endpoint);

          return file_url;
        } else {
          throw Error(
            res?.errors?.join('\n\n') ??
              'createImageUploadOptions: Unknown error.'
          );
        }
      } else {
        return image;
      }
    });

    const updatedImageUrls = Promise.all(updatedImageUrlPromises);

    return updatedImageUrls;
  };

  const _uploading = ref(false);
  const _error = ref<Error | null>();

  const uploadImages = async (images: ContentImage[]) => {
    try {
      _error.value = null;
      _uploading.value = true;
      return await validateAndUploadImages(images);
    } catch (err) {
      _error.value =
        err instanceof Error
          ? err
          : new Error('Error: uploadImages ' + JSON.stringify(err));
      throw err;
    } finally {
      _uploading.value = false;
    }
  };

  return {
    uploading: computed(() => _uploading.value),
    error: computed(() => _error.value),
    uploadImages
  };
};

export const useProfileImageUpload = () => {
  const { dispatchMutationWithTosRetry } = useCommunityDispatchUtils();

  const { mutate: dispatchCreatePostImageOptions } = useMutation(
    createProfileImageUploadOptionsMutation,
    {
      clientId: 'gateway'
    }
  );

  const validateAndUploadImages = async (blob: Blob) => {
    // Note: Size validation is done in the component, to allow for dimention change & quality lowering.

    const imageType = imageMimeTypeToImageType(blob.type);

    if (!imageType) {
      throw new Error(
        `Profile image is of unsupported type, got "${blob.type}".`
      );
    }

    const res = await dispatchMutationWithTosRetry(() =>
      dispatchCreatePostImageOptions({
        input: {
          image_type: imageType
        }
      })
    );

    if (res?.data?.users?.createImageUploadOptions) {
      const { file_url, upload_endpoint } =
        res.data.users.createImageUploadOptions;

      await uploadImageToS3(blob, upload_endpoint);

      return file_url;
    } else {
      throw Error(
        res?.errors?.join('\n\n') ?? 'createImageUploadOptions: Unknown error.'
      );
    }
  };

  const _uploading = ref(false);
  const _error = ref<Error | null>();

  const uploadImage = async (data: Blob) => {
    try {
      _error.value = null;
      _uploading.value = true;

      return await validateAndUploadImages(data);
    } catch (err) {
      _error.value =
        err instanceof Error
          ? err
          : new Error('Error: uploadImages ' + JSON.stringify(err));
      throw err;
    } finally {
      _uploading.value = false;
    }
  };

  return {
    uploading: computed(() => _uploading.value),
    error: computed(() => _error.value),
    uploadImage
  };
};

const uploadImageToS3 = async (blob: Blob, s3UploadEndpoint: string) => {
  await fetch(s3UploadEndpoint, {
    method: 'PUT',
    headers: {
      'Content-Type': blob.type
    },
    body: blob
  });
};
